import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { VaMemorableDate } from '@department-of-veterans-affairs/component-library/dist/react-bindings';

import SubTaskContainerHeader from '../SubTaskContainerHeader';
import { isValidDate, isDateComplete, getDiffInDays } from '../utils';
import { usePathHistory } from '../utils/tracking/PathHistoryContext';
import {
  trackScreenerStepView,
  trackSeparationDateEntered,
  trackScreenerValidationError,
} from '../../utils/tracking/features/screenerTracking';
import pageNames from './pageNames';

// BDD (Benefits Delivery at Discharge) eligibility window, in days until the
// veteran's release from active duty. A separation date 90–180 days out
// qualifies for BDD; fewer than 90 days continues as a standard claim; more
// than 180 days is too early to file at all.
const BDD_MIN_DAYS = 90;
const BDD_MAX_DAYS = 180;

const missingDateErrorMessage = 'Enter your anticipated separation date';
const pastDateErrorMessage = 'Enter a separation date that’s in the future';

const validate = ({ radDate }) => isValidDate(radDate);

// Route based on the BDD eligibility window. Pure, because the resume lookup
// calls it too; the handoff pages save the separation date for the form.
const getNextPage = ({ radDate }) => {
  if (!isValidDate(radDate)) {
    return '';
  }

  const daysUntilSeparation = getDiffInDays(radDate) + 1;

  if (
    daysUntilSeparation >= BDD_MIN_DAYS &&
    daysUntilSeparation <= BDD_MAX_DAYS
  ) {
    return pageNames.fileBdd;
  }

  // Separating in more than 180 days is too early to file a 526 at all.
  if (daysUntilSeparation > BDD_MAX_DAYS) {
    return pageNames.unableToFileBdd;
  }

  // Fewer than 90 days out: too late for BDD, but they can still file a
  // standard claim.
  return pageNames.claimType;
};

/**
 * Release date — captures the veteran's expected release from active duty and
 * routes into the BDD flow when the separation date falls in the 90–180 day
 * window. First page of the active-duty branch of the screener.
 * @param {Object} data - subtask data
 * @param {Boolean} error - page submitted & error state
 * @param {Function} setPageData - updates subtask data
 * @returns {JSX}
 */
const ReleaseDatePage = ({ data = {}, error, setPageData }) => {
  const { addStep, getHistory } = usePathHistory();
  const [dateError, setDateError] = useState(null);

  useEffect(() => {
    trackScreenerStepView({
      stepName: pageNames.releaseDate,
      pathHistoryContext: { addStep, getHistory },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkValid = useCallback(value => {
    if (isValidDate(value)) {
      setDateError(null);
      return;
    }
    setDateError(
      isDateComplete(value) ? pastDateErrorMessage : missingDateErrorMessage,
    );
  }, []);

  useEffect(() => {
    checkValid(data.radDate);
  }, [checkValid, data.radDate]);

  useEffect(() => {
    if (error && dateError) {
      trackScreenerValidationError({
        pageName: pageNames.releaseDate,
        fieldName: 'separation-date',
        errorMessage: dateError,
      });
    }
  }, [error, dateError]);

  const onChange = event => {
    const value = event.target.value || '';
    setPageData({ radDate: value });
    checkValid(value);
  };

  // va-memorable-date only zero-pads month/day internally on blur (see its
  // handleDateBlur), so re-sync radDate here to catch the padded value once
  // the veteran leaves the field.
  const onBlur = event => {
    const value = event.target.value || '';
    setPageData({ radDate: value });
    checkValid(value);
  };

  return (
    <div id={pageNames.releaseDate}>
      <SubTaskContainerHeader />
      <h2 className="vads-u-margin-top--0">
        What is your anticipated separation date?
      </h2>
      <VaMemorableDate
        required
        label="Anticipated separation date"
        hint="Enter 1 or 2 digits for the month and day and 4 digits for the year. You can give an estimate for these dates, if needed."
        removeDateHint
        className="vads-u-margin-top--0"
        name="discharge-date"
        value={data.radDate}
        onDateChange={onChange}
        onDateBlur={onBlur}
        error={error ? dateError : null}
      />
    </div>
  );
};

ReleaseDatePage.propTypes = {
  data: PropTypes.shape({}),
  error: PropTypes.bool,
  setPageData: PropTypes.func,
};

export default {
  name: pageNames.releaseDate,
  component: ReleaseDatePage,
  validate,
  back: pageNames.militaryStatus,
  next: getNextPage,
  onContinue: ({ radDate }) => {
    if (isValidDate(radDate)) {
      trackSeparationDateEntered({
        daysUntilSeparation: getDiffInDays(radDate) + 1,
      });
    }
  },
};
